import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, Check, CheckCheck, User, Clock, AlertTriangle, Calendar, Trash2, PhoneCall, PhoneOff, BarChart3 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/helpers';

// Notification type icons and colors
const getNotificationMeta = (type, t) => {
  switch (type) {
    case 'lead_assigned':
      return { icon: User, color: 'blue', label: t('notifLabelNewLead') };
    case 'stale_lead_reminder':
      return { icon: Clock, color: 'amber', label: t('notifLabelFollowUp') };
    case 'stale_lead_escalation':
      return { icon: AlertTriangle, color: 'red', label: t('notifLabelEscalation') };
    case 'site_visit_reminder':
      return { icon: Calendar, color: 'green', label: t('notifLabelSiteVisit') };
    case 'upcoming_call':
      return { icon: PhoneCall, color: 'blue', label: t('notifLabelUpcomingCall') };
    case 'call_overdue':
      return { icon: PhoneOff, color: 'red', label: t('notifLabelOverdueCall') || 'Missed Call' };
    case 'daily_summary':
      return { icon: BarChart3, color: 'purple', label: t('notifLabelDailySummary') || 'Daily Report' };
    default:
      return { icon: Bell, color: 'gray', label: t('notifLabelDefault') };
  }
};

// Format relative time
const formatTimeAgo = (timestamp, t) => {
  if (!timestamp) return '';

  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('timeJustNow');
  if (diffMins < 60) return t('timeMinutesAgo').replace('{min}', diffMins);
  if (diffHours < 24) return t('timeHoursAgo').replace('{hr}', diffHours);
  if (diffDays < 7) return t('timeDaysAgo').replace('{day}', diffDays);
  return formatDate(date);
};

const getDisplayMessage = (notification) => {
  const msg = notification?.message || '';
  // Hide embedded JSON payload used by DailySummaryModal parsing
  const idx = msg.indexOf('||');
  if (idx !== -1) return msg.slice(0, idx).trim();
  return msg;
};

export default function NotificationBell({
  notifications = [],
  onMarkRead,
  onMarkAllRead,
  onDelete,
  onNotificationClick,
  currentUser,
  hasMore,
  loadMore,
  loadingMore,
  totalCount
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { t } = useLanguage();

  // Filter notifications for current user
  // API already filters for current user, so just use the list
  const userNotifications = notifications;

  const unreadCount = userNotifications.filter(n => !n.read).length;

  const prevUnreadCountRef = useRef(unreadCount);

  // Play sound when unread notification count increases
  useEffect(() => {
    if (unreadCount > prevUnreadCountRef.current) {
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(err => console.log('Audio playback prevented:', err));
      } catch (err) {
        console.log('Error creating Audio:', err);
      }
    }
    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      onMarkRead?.(notification.id);
    }
    onNotificationClick?.(notification);
    setIsOpen(false);
  };

  const handleMarkAllRead = (e) => {
    e.stopPropagation();
    onMarkAllRead?.();
    // setIsOpen(false); // Keep open as per user request
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-full transition-all ${isOpen
          ? 'bg-blue-100 text-blue-600'
          : 'hover:bg-gray-100 text-gray-600'
          }`}
      >
        <Bell className="w-5 h-5" />

        {/* Unread Badge (Red Dot) */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden animate-scale-in origin-top-right">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 backdrop-blur-sm">
            <div>
              <h3 className="font-bold text-gray-900">{t('notifications')}</h3>
              <p className="text-xs text-gray-500">
                {unreadCount > 0
                  ? t('unreadCount').replace('{count}', unreadCount)
                  : t('allCaughtUp')}
                {totalCount > 0 && ` • ${t('totalCount').replace('{count}', totalCount)}`}
              </p>
            </div>
            {userNotifications.length > 0 && (
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    {t('markAllRead')}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Delete all notifications one by one (optimistic)
                    userNotifications.forEach(n => onDelete?.(n.id));
                  }}
                  className="text-xs text-red-400 hover:text-red-600 font-medium flex items-center gap-1 transition-colors"
                  title={t('clearAllTooltip')}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('clearAll')}
                </button>
              </div>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
            {userNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400 flex flex-col items-center">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                  <Bell className="w-8 h-8 opacity-20" />
                </div>
                <p className="font-medium text-gray-900">{t('noNotifications')}</p>
                <p className="text-xs mt-1">{t('youreAllCaughtUp')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 flex flex-col">
                {userNotifications.map((notification) => {
                  const meta = getNotificationMeta(notification.type, t);
                  const IconComponent = meta.icon;

                  return (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`relative px-4 py-3 cursor-pointer transition-all duration-300 hover:bg-gray-50 group ${!notification.read ? 'bg-yellow-50' : 'opacity-60 hover:opacity-100'
                        }`}
                    >
                      {/* Unread Indicator Border */}
                      {!notification.read && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-yellow-400"></div>
                      )}

                      {/* Delete Button (visible on hover) */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete?.(notification.id);
                        }}
                        className="absolute top-2 right-2 p-1 rounded-full text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                        title={t('removeNotification')}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>

                      <div className="flex gap-3 pr-5">
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full bg-${meta.color}-100 flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <IconComponent className={`w-5 h-5 text-${meta.color}-600`} />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start gap-2">
                            <p className={`text-sm leading-snug ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                              {getDisplayMessage(notification)}
                            </p>
                            <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                              {formatTimeAgo(notification.createdAt, t)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 mt-1.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-${meta.color}-50 text-${meta.color}-700 font-medium border border-${meta.color}-100`}>
                              {meta.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Load More Button */}
                {hasMore && (
                  <div className="p-3 text-center border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        loadMore?.();
                      }}
                      disabled={loadingMore}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium py-1 px-3 rounded hover:bg-blue-50 transition-colors disabled:opacity-50"
                    >
                      {loadingMore
                        ? t('loadingMore')
                        : t('loadMoreCount').replace('{count}', totalCount - notifications.length)}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
